import type { EventLogEntry } from '../../types/council';

export type LogFilter = 'all' | 'governance' | 'turns' | 'squad';

export interface LogState {
  entries: EventLogEntry[];          // DESC by seq
  unread: number;
  filter: LogFilter;
  expandedSquads: Record<string, boolean>;
}

export const initialState: LogState = {
  entries: [], unread: 0, filter: 'all', expandedSquads: {},
};

export type Action =
  | { type: 'BACKFILL'; entries: EventLogEntry[] }
  | { type: 'MERGE'; entries: EventLogEntry[] }     // reconnect catch-up
  | { type: 'LIVE'; entry: EventLogEntry }
  | { type: 'SET_FILTER'; filter: LogFilter }
  | { type: 'TOGGLE_SQUAD'; squadId: string }
  | { type: 'MARK_READ' };

const GOVERNANCE = new Set([
  'cascade_depth_exceeded', 'budget_overshoot', 'proposal_queued',
  'proposal_resolved', 'petition_filed', 'peer_turn_failed', 'merge_turn_failed',
]);
const SQUAD_PREFIX = 'chat_squad_';

export function categoryOf(eventType: string): LogFilter {
  if (GOVERNANCE.has(eventType)) return 'governance';
  if (eventType.startsWith('governor_')) return 'governance';   // G3b governor decisions
  if (eventType.startsWith(SQUAD_PREFIX)) return 'squad';
  return 'turns';
}

// G3b — projected lifecycle status per governor decision_id (the FE single source).
export const STATUS_OF_EVENT: Record<string, string> = {
  governor_proposed: 'pending', governor_recommended: 'advised',
  governor_applied: 'applied', governor_dismissed: 'dismissed',
};

/** Highest-seq GOVERNOR_* event per decision_id → its projected status. Drives
 *  inline Approve/Dismiss enablement (a live `applied`/`dismissed` frame out-ranks
 *  any synthesized pending row, so buttons flip correctly). */
export function decisionStatuses(state: LogState): Record<string, string> {
  const best: Record<string, { seq: number; status: string }> = {};
  for (const e of state.entries) {                       // DESC by seq
    const id = (e.target as any)?.decision_id as string | undefined;
    const status = STATUS_OF_EVENT[e.event_type];
    if (!id || !status) continue;
    if (best[id] == null || e.seq > best[id].seq) best[id] = { seq: e.seq, status };
  }
  return Object.fromEntries(Object.entries(best).map(([k, v]) => [k, v.status]));
}

export function mergeBySeq(existing: EventLogEntry[], incoming: EventLogEntry[]): EventLogEntry[] {
  const bySeq = new Map<number, EventLogEntry>();
  for (const e of existing) bySeq.set(e.seq, e);
  for (const e of incoming) bySeq.set(e.seq, e);
  return Array.from(bySeq.values()).sort((a, b) => b.seq - a.seq);
}

export function reducer(state: LogState, action: Action): LogState {
  switch (action.type) {
    case 'BACKFILL':
      return { ...state, entries: mergeBySeq([], action.entries) };
    case 'MERGE':
      return { ...state, entries: mergeBySeq(state.entries, action.entries) };
    case 'LIVE': {
      if (state.entries.some(e => e.seq === action.entry.seq)) return state;
      return { ...state, entries: mergeBySeq(state.entries, [action.entry]),
               unread: state.unread + 1 };
    }
    case 'SET_FILTER':
      return { ...state, filter: action.filter };
    case 'TOGGLE_SQUAD':
      return { ...state, expandedSquads: {
        ...state.expandedSquads, [action.squadId]: !state.expandedSquads[action.squadId] } };
    case 'MARK_READ':
      return { ...state, unread: 0 };
    default:
      return state;
  }
}

export type LogRow =
  | { kind: 'event'; entry: EventLogEntry }
  | { kind: 'squad-cluster'; squadId: string; dispatch: EventLogEntry | null;
      memberCount: number; members: EventLogEntry[]; expanded: boolean };

export function visibleRows(state: LogState): LogRow[] {
  const filtered = state.filter === 'all'
    ? state.entries
    : state.entries.filter(e => categoryOf(e.event_type) === state.filter);

  const rows: LogRow[] = [];
  const seenSquads = new Set<string>();
  for (const e of filtered) {
    const isMember = e.event_type.startsWith(SQUAD_PREFIX) && e.event_type !== 'chat_squad_dispatched' && e.squad_id;
    const isDispatch = e.event_type === 'chat_squad_dispatched' && e.squad_id;
    if ((isMember || isDispatch) && e.squad_id) {
      if (seenSquads.has(e.squad_id)) continue;
      seenSquads.add(e.squad_id);
      const group = filtered.filter(x => x.squad_id === e.squad_id);
      const dispatch = group.find(x => x.event_type === 'chat_squad_dispatched') || null;
      const members = group.filter(x => x !== dispatch);
      rows.push({ kind: 'squad-cluster', squadId: e.squad_id, dispatch,
                  memberCount: members.length, members,
                  expanded: !!state.expandedSquads[e.squad_id] });
    } else {
      rows.push({ kind: 'event', entry: e });
    }
  }
  return rows;
}

/** Per-peer cost fold over the live event store. Filters turn_added rows with a
 *  persona, sums actual-or-estimate cost, keeps the latest model_slug. Pure;
 *  re-runs cheaply as new events land via the WS subscription. */
export function peerCostByPersonaId(
  entries: EventLogEntry[],
): Map<string, { costUsd: number; modelSlug: string | null; turns: number }> {
  const out = new Map<string, { costUsd: number; modelSlug: string | null; turns: number }>();
  for (const e of entries) {                       // DESC by seq → first seen = latest model
    if (e.event_type !== 'turn_added' || !e.source_persona_id) continue;
    const t = e.target as { cost_usd?: string | null; cost_actual_usd?: string | null; model_slug?: string | null };
    const raw = t.cost_actual_usd ?? t.cost_usd;
    const cost = raw != null ? parseFloat(raw) : NaN;
    const prev = out.get(e.source_persona_id);
    out.set(e.source_persona_id, {
      costUsd: (prev?.costUsd ?? 0) + (Number.isFinite(cost) ? cost : 0),
      modelSlug: prev?.modelSlug ?? t.model_slug ?? null,
      turns: (prev?.turns ?? 0) + 1,
    });
  }
  return out;
}
